import {
  PlatformAppEvent,
  PlatformAppEventEmitter,
} from '@wix/public-editor-platform-events';
import { EventType } from '@wix/public-editor-platform-interfaces';

import {
  createEditorPlatformApplicationContainerError,
  EditorPlatformApplicationContainerErrorCode,
} from './errors';
import { EditorPlatformApplication } from '@wix/editor-application';
import { type IApplicationSpec, type IPrivateAPIFixMe } from '../../types';
import { executeApplication } from './executeApplication';

export class PlatformApplicationContainer {
  #apps: Record<string, EditorPlatformApplication> = {};

  readonly #privateAPI: IPrivateAPIFixMe;
  readonly #events: PlatformAppEventEmitter;

  constructor(privateApi: IPrivateAPIFixMe, events: PlatformAppEventEmitter) {
    this.#privateAPI = privateApi;
    this.#events = events;

    this.#events.subscribe((event) => {
      switch (event.type) {
        case PlatformAppEvent.HostEvent: {
          if (event.payload.type === EventType.removeAppCompleted) {
            void this.#events.withEvent(
              this.#events.factories.createApplicationRemovedEvent(
                event.payload.appDefinitionId!,
              ),
              () => {
                return this.removeApplication(event.payload.appDefinitionId!);
              },
            );
          }
          break;
        }
      }
    });
  }

  public async runApplication(appSpec: IApplicationSpec) {
    const bundle = await this.loadApplication(appSpec);
    const instance = await this.executeApplication(appSpec, bundle);

    this.setApplication(appSpec.appDefinitionId, instance);

    return instance;
  }

  private setApplication(
    appDefId: string,
    instance: EditorPlatformApplication,
  ) {
    this.#apps[appDefId] = instance;

    void this.#events.withEvent(
      this.#events.factories.createApplicationApiInitEvent(
        appDefId,
        // TODO: both types are set here...
        // @ts-expect-error TODO: fix me
        instance?.api?.private ? 'private' : 'public',
      ),
      () => {
        // NOTE: sometimes I saw this method was called while application was not executed
        this.#privateAPI.applicationManager.setApplication(instance);
      },
    );
  }

  public getApplication(appDefId: string) {
    return this.#apps[appDefId];
  }

  public getAppDefinitionIds() {
    return Object.keys(this.#apps);
  }

  private removeApplication(appDefinitionId: string) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.#apps[appDefinitionId];
  }

  private async loadApplication(appSpec: IApplicationSpec) {
    const url = appSpec.url;

    return this.#events.withEvent(
      this.#events.factories.createApplicationLoadEvent(appSpec, url),

      async () => {
        try {
          return await this.loadApplicationBundle(url);
        } catch (e) {
          throw createEditorPlatformApplicationContainerError(
            EditorPlatformApplicationContainerErrorCode.ApplicationLoadError,
          )
            .withUrl(url)
            .withAppDefinitionId(appSpec.appDefinitionId)
            .withParentError(e as any);
        }
      },
    );
  }

  private async loadApplicationBundle(url: string) {
    /**
     * NOTE: we don't use wix http client here
     * because this code is public, while http client is private
     */
    const res = await fetch(url, {
      method: 'GET',
    });

    const isSuccessfulResponse = res.status >= 200 && res.status <= 299;

    if (!isSuccessfulResponse) {
      throw createEditorPlatformApplicationContainerError(
        EditorPlatformApplicationContainerErrorCode.ApplicationFetchError,
      ).withUrl(url);
    }

    return res.text();
  }

  private async executeApplication(appSpec: IApplicationSpec, bundle: string) {
    return this.#events.withEvent(
      this.#events.factories.createApplicationExecuteEvent(
        appSpec,
        appSpec.url,
      ),
      async () => {
        const { instance } = await executeApplication(
          this.#events,
          appSpec,
          bundle,
        );
        return instance;
      },
    );
  }
}
